// imports
import toastr from 'toastr';
import { AnyAction } from '@reduxjs/toolkit';
import React, { useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Popup from '../../atoms/popup';
import { ROUTES, TEXTS } from '../../constants';
import { IModal, Status } from '../../common/types';
import { useConnection } from '../../repositories/web3/session';
import { logUserProperties } from '../../services/firebase/analytics';
import {
  fetchConfig, fetchPreferences, fetchDelegateWallet, updateDelegateWallet
} from '../../repositories/store/thunks';
import {
  selectConfig, selectUserId, selectUserPreferences, selectUserDelegateWallet
} from '../../repositories/store/slices';

// interfaces / types / consts
toastr.options = {
  timeOut: 0,
  closeButton: true,
  newestOnTop: true,
  extendedTimeOut: 0,
  preventDuplicates: true,
  toastClass: 'bg-success',
  positionClass: 'toast-bottom-right'
};

// exports
export default function GameStatePopup(): JSX.Element {
  // hooks configuration
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const modalRef = useRef<IModal>();
  const id = useSelector(selectUserId);
  const { state: path } = useLocation();
  const { address, reconnected } = useConnection();
  const { fetchStatus: fetchConfigStatus, fetctError: fetchConfigError } = useSelector(selectConfig);
  const { fetchError: fetchUserPrefError, fetchStatus: fetchUserPrefStatus } = useSelector(selectUserPreferences);
  const {
    wallet: delegateWallet,
    fetchError: fetchDelegatWalletError,
    fetchStatus: fetchDelegatWalletStatus,
    updateError: updateDelegatWalletError,
    updateStatus: updateDelegatWalletStatus
  } = useSelector(selectUserDelegateWallet);

  useEffect(() => { // handle configurations
    if (!address) {
      modalRef.current?.hide();
    } else if (fetchConfigStatus === 'idle') {
      dispatch(fetchConfig() as unknown as AnyAction);
      modalRef.current?.show();
    } else if (fetchConfigStatus === 'success' && fetchUserPrefStatus !== 'loading' && address && address !== id) {
      logUserProperties({ walletId: address });
      dispatch(fetchPreferences(address!) as unknown as AnyAction);
      modalRef.current?.show();
    } else if (fetchUserPrefStatus === 'success' && fetchDelegatWalletStatus === 'idle') {
      dispatch(fetchDelegateWallet() as unknown as AnyAction);
      modalRef.current?.show();
    } else if (delegateWallet) {
      modalRef.current?.hide();
    }
  });
  useEffect(() => { // handle reconnect
    if (reconnected === false && fetchUserPrefStatus === 'success') {
      if (delegateWallet) { navigate(path || ROUTES.GAME, { state: {} }); }
    }
  });

  // private methods
  const onRegisterButtonClick = (): void => { dispatch(updateDelegateWallet() as unknown as AnyAction); };

  // popup props
  const configMessage = fetchConfigError || `Load${(fetchConfigStatus === 'success') ? 'ed' : 'ing'} game world...`;
  const prefMessage = fetchUserPrefError || `Prepar${(fetchUserPrefStatus === 'success') ? 'ed' : 'ing'} your adventure...`;
  const delegateWalletMessage = fetchDelegatWalletError || `Fetch${(fetchDelegatWalletStatus === 'success') ? 'ed' : 'ing'} delegate wallet!`;
  let message = '';
  let btnText = '';
  let status = 'idle';
  let title = 'Standby';
  if (fetchUserPrefStatus === 'idle') {
    message = configMessage;
    status = fetchConfigStatus;
  } else if (fetchDelegatWalletStatus === 'idle') {
    message = prefMessage;
    status = fetchUserPrefStatus;
  } else if (fetchDelegatWalletStatus !== 'success' && fetchUserPrefStatus === 'success') {
    message = delegateWalletMessage;
    status = fetchDelegatWalletStatus;
  } else if (fetchDelegatWalletStatus === 'success' && !delegateWallet) {
    title = 'Gas-less Gameplay';
    btnText = 'Register Wallet';
    status = updateDelegatWalletStatus;
    message = TEXTS.WALLET_REGISTER;
    if (updateDelegatWalletError) {
      btnText = '';
      status = 'error';
      message = updateDelegatWalletError;
    }
  } else if (delegateWallet) {
    btnText = '';
    status = 'success';
    message = TEXTS.POPUP_CONFIGURATION_LOADED;
  }

  // render
  return (<Popup ref={modalRef} status={status as Status} title={title} message={message} btnText={btnText} onClick={onRegisterButtonClick} />);
}
